import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Button,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Card,
  Typography,
} from '@mui/material';
import { RiCloseLine, RiAspectRatioLine } from '@remixicon/react';
import { setOrientation, setBackground } from '../../redux/slices/canvasSlice';

/**
 * Canvas Properties Modal
 * 
 * Controls:
 * - Width/Height inputs with aspect ratio lock
 * - Landscape/Portrait presets
 * - Background color/image selector
 */
const CanvasPropertiesModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { orientation, dimensions, background } = useSelector((state) => state.canvas);
  
  const [width, setWidth] = useState(dimensions[orientation].width);
  const [height, setHeight] = useState(dimensions[orientation].height);
  const [bgTab, setBgTab] = useState(0);
  const [selectedColor, setSelectedColor] = useState('#FFFFFF');
  
  const handleLandscape = () => {
    setWidth(1920);
    setHeight(1080);
    dispatch(setOrientation('landscape'));
  };

  const handlePortrait = () => {
    setWidth(1080);
    setHeight(1920);
    dispatch(setOrientation('portrait'));
  };

  const handleAspectRatio = () => {
    setHeight(width);
  };

  const handleApplyColor = () => {
    dispatch(setBackground({ type: 'color', value: selectedColor }));
  };

  const presetColors = [
    '#FFFFFF', '#F5F5F5', '#E0E0E0', '#000000',
    '#C61F2F', '#E42436', '#6B22D5', '#5B1DB7',
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Canvas Properties
          <IconButton onClick={onClose} size="small">
            <RiCloseLine size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Width & Height */}
        <Box sx={{ marginBottom: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 2 }}>
            <TextField
              label="Width (W)"
              type="number"
              value={width}
              onChange={(e) => setWidth(parseInt(e.target.value) || 0)}
              size="small"
              fullWidth
            />
            <TextField
              label="Height (H)"
              type="number"
              value={height}
              onChange={(e) => setHeight(parseInt(e.target.value) || 0)}
              size="small"
              fullWidth
            />
            <IconButton
              onClick={handleAspectRatio}
              size="small"
              sx={{
                border: '1px solid #E0E0E0',
                borderRadius: 1,
              }}
            >
              <RiAspectRatioLine size={20} />
            </IconButton>
          </Box>

          {/* Presets */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={orientation === 'landscape' ? 'contained' : 'outlined'}
              onClick={handleLandscape}
              fullWidth
            >
              Landscape (1920×1080)
            </Button>
            <Button
              variant={orientation === 'portrait' ? 'contained' : 'outlined'}
              onClick={handlePortrait}
              fullWidth
            >
              Portrait (1080×1920)
            </Button>
          </Box>
        </Box>

        {/* Background Selector */}
        <Box>
          <Tabs value={bgTab} onChange={(e, v) => setBgTab(v)} sx={{ marginBottom: 2 }}>
            <Tab label="Colors" />
            <Tab label="Backgrounds" />
          </Tabs>

          {/* Colors Tab */}
          {bgTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                <TextField
                  type="color"
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  sx={{ width: 80 }}
                />
                <TextField
                  label="Color Value"
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>

              <Grid container spacing={1} sx={{ marginBottom: 2 }}>
                {presetColors.map((color) => (
                  <Grid item key={color}>
                    <Box
                      onClick={() => setSelectedColor(color)}
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: color,
                        border: selectedColor === color ? '3px solid #6B22D5' : '1px solid #E0E0E0',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s',
                      }}
                    />
                  </Grid>
                ))}
              </Grid>

              <Button
                variant="contained"
                onClick={handleApplyColor}
                fullWidth
              >
                Apply Color
              </Button>
            </Box>
          )}

          {/* Backgrounds Tab */}
          {bgTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" align="center">
                Background templates will be loaded from AWS S3
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CanvasPropertiesModal;


